/**
 * usePageView — auto-tracks a page_viewed analytics event.
 *
 * Fires once on mount and again whenever `pagePath` changes.
 * Safe to call in SSR environments — the track call is a no-op when the
 * singleton tracker has not been initialised or analytics is disabled.
 */

import { useEffect } from 'react';
import { useAnalytics } from './useAnalytics';
import { EVENT_TYPES } from '../events/event-catalog';
import type { PageViewedData } from '../events/domains/navigation-events';

/**
 * Automatically tracks a `navigation.page_viewed.v1` event.
 *
 * @param pagePath - Explicit path override.  When omitted, falls back to
 *   `window.location.pathname` in browser environments.
 *
 * @example
 * // In a page component:
 * usePageView('/products/my-product');
 *
 * // Or let the hook read window.location.pathname automatically:
 * usePageView();
 */
export function usePageView(pagePath?: string): void {
  const { track } = useAnalytics();

  useEffect(() => {
    const resolvedPath =
      pagePath ??
      (typeof window !== 'undefined' ? window.location.pathname : null);

    if (resolvedPath === null) return;

    const data: PageViewedData = {
      page_path: resolvedPath,
      page_title:
        typeof document !== 'undefined' ? document.title : null,
      referrer:
        typeof document !== 'undefined' ? document.referrer || null : null,
      viewport_width:
        typeof window !== 'undefined' ? window.innerWidth : null,
      viewport_height:
        typeof window !== 'undefined' ? window.innerHeight : null,
    };

    track(EVENT_TYPES.PAGE_VIEWED, data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagePath]);
}
